import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { formatDateTime } from '../../../lib/format';
import {
  Gavel, Plus, RefreshCw, Search, Calendar, Users, PlayCircle, Square,
  Trophy, ArrowLeft, X, Send, Activity, Clock,
} from 'lucide-react';

const STATUS_TONE = {
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  live: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed: 'bg-amber-100 text-amber-800 border-amber-200',
  awarded: 'bg-purple-100 text-purple-800 border-purple-200',
};

const StatusBadge = ({ status }) => (
  <span data-testid={`badge-${status}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${STATUS_TONE[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
    {status || 'unknown'}
  </span>
);

const fmt = (iso) => {
  if (!iso) return '—';
  try { return formatDateTime(iso); } catch { return iso; }
};
const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// ────────────────────────────────────────────────────────────────────────
// Create Auction Wizard
// ────────────────────────────────────────────────────────────────────────
const CreateAuctionDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', project_id: '', category: '',
    start_at: '', end_at: '',
    item_lines: [{ item_code: '', qty: '', uom: '' }],
    rules: { min_decrement_pct: 1, auto_extend_minutes: 5, show_rank: true, show_competitor_count: true, show_competitor_prices: false },
  });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({
    title: '', project_id: '', category: '',
    start_at: '', end_at: '',
    item_lines: [{ item_code: '', qty: '', uom: '' }],
    rules: { min_decrement_pct: 1, auto_extend_minutes: 5, show_rank: true, show_competitor_count: true, show_competitor_prices: false },
  });

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.start_at || !form.end_at) return toast.error('Start and end times are required');
    if (new Date(form.end_at) <= new Date(form.start_at)) return toast.error('End must be after start');
    const validLines = form.item_lines.filter(l => l.item_code && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error('At least one item line with code and qty > 0');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        project_id: form.project_id || null,
        category: form.category || 'General',
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        item_lines: validLines.map(l => ({ item_code: l.item_code.trim(), qty: Number(l.qty), uom: l.uom || 'Nos' })),
        rules: form.rules,
      };
      const created = await apiPost('/procurement/auctions', payload);
      toast.success(`Auction ${created.auction_no || ''} created`);
      reset();
      onCreated(created);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create auction');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="create-auction-dialog">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 bg-white">
          <div className="font-display font-bold text-lg text-[#3b4872]">New Reverse Auction</div>
          <button data-testid="dialog-close" onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Title <span className="text-red-500">*</span></span>
              <input data-testid="auction-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. TMT Steel Bars Q4 Bulk"
                className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Category</span>
              <input data-testid="auction-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="Steel / Cement / Diesel…"
                className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Start <span className="text-red-500">*</span></span>
              <input data-testid="auction-start" type="datetime-local" value={form.start_at}
                onChange={e => setForm({ ...form, start_at: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">End <span className="text-red-500">*</span></span>
              <input data-testid="auction-end" type="datetime-local" value={form.end_at}
                onChange={e => setForm({ ...form, end_at: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
            </label>
          </div>

          {/* Item lines */}
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Items at auction <span className="text-red-500">*</span></div>
            <div className="space-y-2">
              {form.item_lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-line-${idx}`}>
                  <input placeholder="Item code" value={line.item_code}
                    onChange={e => {
                      const copy = [...form.item_lines]; copy[idx] = { ...line, item_code: e.target.value }; setForm({ ...form, item_lines: copy });
                    }}
                    className="col-span-6 border border-slate-300 rounded-sm p-2 text-sm" />
                  <input placeholder="Qty" type="number" min="0" value={line.qty}
                    onChange={e => {
                      const copy = [...form.item_lines]; copy[idx] = { ...line, qty: e.target.value }; setForm({ ...form, item_lines: copy });
                    }}
                    className="col-span-3 border border-slate-300 rounded-sm p-2 text-sm" />
                  <input placeholder="UOM" value={line.uom}
                    onChange={e => {
                      const copy = [...form.item_lines]; copy[idx] = { ...line, uom: e.target.value }; setForm({ ...form, item_lines: copy });
                    }}
                    className="col-span-2 border border-slate-300 rounded-sm p-2 text-sm" />
                  <button onClick={() => setForm({ ...form, item_lines: form.item_lines.filter((_, i) => i !== idx) })}
                    disabled={form.item_lines.length === 1}
                    className="col-span-1 p-1 text-red-500 hover:bg-red-50 disabled:opacity-30 rounded-sm">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button data-testid="add-line"
              onClick={() => setForm({ ...form, item_lines: [...form.item_lines, { item_code: '', qty: '', uom: '' }] })}
              className="mt-2 text-sm text-[#3b4872] font-bold hover:underline">+ Add line</button>
          </div>

          {/* Rules */}
          <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
            <div className="text-sm font-bold text-slate-700 mb-3">Auction Rules</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs text-slate-600">Min decrement % per bid</span>
                <input data-testid="rule-min-decrement" type="number" step="0.1" min="0" value={form.rules.min_decrement_pct}
                  onChange={e => setForm({ ...form, rules: { ...form.rules, min_decrement_pct: Number(e.target.value) } })}
                  className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Auto-extend minutes (anti-sniping)</span>
                <input data-testid="rule-auto-extend" type="number" min="0" value={form.rules.auto_extend_minutes}
                  onChange={e => setForm({ ...form, rules: { ...form.rules, auto_extend_minutes: Number(e.target.value) } })}
                  className="mt-1 w-full border border-slate-300 rounded-sm p-2 text-sm" />
              </label>
            </div>
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="rule-show-rank" type="checkbox" checked={form.rules.show_rank}
                  onChange={e => setForm({ ...form, rules: { ...form.rules, show_rank: e.target.checked } })} />
                Show each vendor their own rank
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="rule-show-count" type="checkbox" checked={form.rules.show_competitor_count}
                  onChange={e => setForm({ ...form, rules: { ...form.rules, show_competitor_count: e.target.checked } })} />
                Show total bidder count (no identity)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="rule-show-prices" type="checkbox" checked={form.rules.show_competitor_prices}
                  onChange={e => setForm({ ...form, rules: { ...form.rules, show_competitor_prices: e.target.checked } })} />
                Show anonymous lowest-bid value (use sparingly)
              </label>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancel</button>
          <button data-testid="create-submit" onClick={submit} disabled={saving}
            className="px-4 py-2 text-sm font-bold text-white bg-[#3b4872] hover:bg-[#2d3858] rounded-sm disabled:opacity-60">
            {saving ? 'Creating…' : 'Create Auction'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Invite vendors dialog
// ────────────────────────────────────────────────────────────────────────
const InviteVendorsDialog = ({ auction, open, onClose, onInvited }) => {
  const [q, setQ] = useState('');
  const [vendors, setVendors] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const alreadyInvited = new Set(auction?.invited_vendor_ids || []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/vendors', { q, limit: 50 });
      setVendors((data.items || []).filter(v => !alreadyInvited.has(v.id)));
    } catch (e) {
      toast.error('Vendor search failed');
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { if (open) search(); }, [open, search]);

  const toggle = (id) => setPicked(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const invite = async () => {
    if (picked.size === 0) return toast.error('Pick at least one vendor');
    try {
      await apiPost(`/procurement/auctions/${auction.id}/invite`, { vendor_ids: Array.from(picked) });
      toast.success(`Invited ${picked.size} vendor(s)`);
      setPicked(new Set());
      onInvited();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invite failed'); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-slate-200 max-w-2xl w-full max-h-[80vh] flex flex-col" data-testid="invite-dialog">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <div className="font-display font-bold text-lg text-[#3b4872]">Invite Vendors to {auction?.auction_no}</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
        </div>
        <div className="p-4 border-b">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="vendor-search" value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="Search by name, GSTIN, code…"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-500">Searching…</div>}
          {!loading && vendors.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No vendors match.</div>}
          {vendors.map(v => (
            <label key={v.id} data-testid={`vendor-row-${v.id}`}
              className="flex items-center gap-3 px-6 py-2 hover:bg-slate-50 border-b border-slate-100 cursor-pointer">
              <input type="checkbox" checked={picked.has(v.id)} onChange={() => toggle(v.id)} />
              <div className="flex-1">
                <div className="font-medium text-sm">{v.name}</div>
                <div className="text-xs text-slate-500">{v.vendor_code} · {v.empanelment_status || 'Not Empanelled'} · {v.email || 'No email'}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="border-t px-6 py-3 flex justify-between items-center bg-slate-50">
          <div className="text-xs text-slate-600">{picked.size} selected · {alreadyInvited.size} already invited</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancel</button>
            <button data-testid="invite-submit" onClick={invite} disabled={picked.size === 0}
              className="px-4 py-2 text-sm font-bold text-white bg-[#3b4872] hover:bg-[#2d3858] rounded-sm disabled:opacity-60 flex items-center gap-2">
              <Send size={14} /> Invite
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Detail panel — for one auction
// ────────────────────────────────────────────────────────────────────────
const AuctionDetailPanel = ({ auctionId, onBack, onAnyChange }) => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [awardConfirm, setAwardConfirm] = useState(null); // vendor_id pending confirm

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet(`/procurement/auctions/${auctionId}/live`);
      setLive(data);
    } catch (e) {
      toast.error('Failed to load auction');
    } finally { setLoading(false); }
  }, [auctionId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while live for near-real-time leaderboard (SSE comes in Phase C)
  useEffect(() => {
    if (live?.status !== 'live') return;
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [live?.status, refresh]);

  const startAuction = async () => {
    try {
      await apiPost(`/procurement/auctions/${auctionId}/start`);
      toast.success('Auction started');
      await refresh(); onAnyChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Start failed'); }
  };
  const closeAuction = async () => {
    if (!window.confirm('Close this auction? Vendors will no longer be able to bid.')) return;
    try {
      await apiPost(`/procurement/auctions/${auctionId}/close`);
      toast.success('Auction closed');
      await refresh(); onAnyChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
  };
  const awardTo = async (vendor_id) => {
    try {
      await apiPost(`/procurement/auctions/${auctionId}/award`, { vendor_id });
      toast.success('Auction awarded');
      setAwardConfirm(null);
      await refresh(); onAnyChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Award failed'); }
  };

  if (loading) return <div data-testid="detail-loading" className="p-6 text-slate-500">Loading…</div>;
  if (!live) return null;

  const leaderboard = live.leaderboard || [];
  const invitedCount = (live.invited_vendor_ids || []).length;
  const timeLeft = live.time_remaining_seconds || 0;
  const mins = Math.floor(timeLeft / 60), secs = timeLeft % 60;

  return (
    <div data-testid="auction-detail-panel" className="space-y-5">
      <button data-testid="back-to-list" onClick={onBack}
        className="text-sm text-slate-600 hover:text-[#3b4872] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to auctions
      </button>

      {/* Header */}
      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Auction {live.auction_no}</div>
            <h2 className="font-display text-2xl font-extrabold mt-1 text-[#3b4872]">{live.title}</h2>
            <div className="text-sm text-slate-600 mt-1">{live.category || 'General'} · Created {fmt(live.created_at)}</div>
          </div>
          <StatusBadge status={live.status} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 text-sm">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Start</div><div className="font-medium">{fmt(live.start_at)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">End</div><div className="font-medium">{fmt(live.end_at)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Invited</div><div className="font-medium">{invitedCount} vendors</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Bids placed</div><div className="font-medium">{live.total_bidders || 0}</div></div>
        </div>
        {live.status === 'live' && (
          <div className="mt-4 rounded-sm bg-emerald-50 border border-emerald-200 px-3 py-2 inline-flex items-center gap-2 text-sm font-medium text-emerald-800">
            <Clock size={14} /> Time remaining: <span data-testid="time-remaining" className="font-mono font-bold">{mins}m {secs}s</span>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap gap-2">
          {live.status === 'scheduled' && (
            <>
              <button data-testid="open-invite" onClick={() => setInviteOpen(true)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1">
                <Users size={14} /> Invite Vendors
              </button>
              <button data-testid="start-auction" onClick={startAuction} disabled={invitedCount === 0}
                title={invitedCount === 0 ? 'Invite at least one vendor first' : 'Start auction'}
                className="px-3 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm disabled:opacity-50 flex items-center gap-1">
                <PlayCircle size={14} /> Start Auction
              </button>
            </>
          )}
          {live.status === 'live' && (
            <button data-testid="close-auction" onClick={closeAuction}
              className="px-3 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-sm flex items-center gap-1">
              <Square size={14} /> Close Auction
            </button>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="rounded-sm border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-bold text-[#3b4872] flex items-center gap-2">
            <Activity size={16} /> Leaderboard
            {live.status === 'live' && <span className="text-xs font-normal text-emerald-600">· auto-refresh every 6s</span>}
          </div>
          <button onClick={refresh} className="text-xs text-slate-500 hover:text-[#3b4872] inline-flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3 w-12">Rank</th>
              <th className="text-left p-3">Vendor</th>
              <th className="text-right p-3">Bid (₹)</th>
              <th className="text-left p-3">Last bid</th>
              {live.status === 'closed' && <th className="text-right p-3">Action</th>}
            </tr>
          </thead>
          <tbody>
            {leaderboard.length === 0 && (
              <tr><td colSpan={live.status === 'closed' ? 5 : 4} className="p-8 text-center text-slate-500">No bids placed yet.</td></tr>
            )}
            {leaderboard.map(b => (
              <tr key={b.id || b.vendor_id} data-testid={`bid-row-${b.vendor_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-bold">{b.rank === 1 ? <span className="text-emerald-700 inline-flex items-center gap-1"><Trophy size={14} /> 1</span> : b.rank}</td>
                <td className="p-3">{b.vendor_name || b.vendor_id}</td>
                <td className="p-3 text-right font-mono font-bold">{inr(b.total_value)}</td>
                <td className="p-3 text-xs text-slate-500">{fmt(b.created_at || b.updated_at)}</td>
                {live.status === 'closed' && (
                  <td className="p-3 text-right">
                    <button data-testid={`award-${b.vendor_id}`} onClick={() => setAwardConfirm(b)}
                      className="px-3 py-1.5 text-xs font-bold text-white bg-[#3b4872] hover:bg-[#2d3858] rounded-sm">
                      Award
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {live.status === 'awarded' && live.awarded_vendor_id && (
          <div className="px-5 py-3 border-t border-slate-200 bg-purple-50 text-sm">
            <Trophy className="inline mr-1" size={14} />
            <b>Awarded to:</b> {leaderboard.find(b => b.vendor_id === live.awarded_vendor_id)?.vendor_name || live.awarded_vendor_id}
            {' '}on {fmt(live.awarded_at)} by {live.awarded_by}
          </div>
        )}
      </div>

      <InviteVendorsDialog auction={live} open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={() => { refresh(); onAnyChange(); }} />

      {/* Award confirmation */}
      {awardConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm border border-slate-200 max-w-md w-full p-6">
            <div className="font-display font-bold text-lg text-[#3b4872] flex items-center gap-2"><Trophy size={20} /> Award auction?</div>
            <p className="text-sm text-slate-600 mt-3">
              Award <b>{live.auction_no}</b> to <b>{awardConfirm.vendor_name || awardConfirm.vendor_id}</b> at <b>{inr(awardConfirm.total_value)}</b>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setAwardConfirm(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancel</button>
              <button data-testid="award-confirm" onClick={() => awardTo(awardConfirm.vendor_id)}
                className="px-4 py-2 text-sm font-bold text-white bg-[#3b4872] hover:bg-[#2d3858] rounded-sm">
                Confirm Award
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────
export default function VendorAuctionsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/procurement/auctions');
      setRows(data.items || []);
    } catch (e) {
      toast.error('Failed to load auctions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => filter === 'all' || r.status === filter);

  if (selectedId) {
    return <AuctionDetailPanel auctionId={selectedId} onBack={() => setSelectedId(null)} onAnyChange={load} />;
  }

  return (
    <div data-testid="vendor-auctions-admin" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Vendor Portal Admin</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Gavel size={26} /> Auctions
          </h1>
          <p className="text-sm text-slate-600 mt-1">Create, invite, monitor and award reverse auctions.</p>
        </div>
        <button data-testid="new-auction" onClick={() => setCreateOpen(true)}
          className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-sm font-bold hover:bg-[#2d3858] flex items-center gap-2">
          <Plus size={16} /> New Auction
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'scheduled', 'live', 'closed', 'awarded'].map(f => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border ${
              filter === f ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
            }`}>
            {f} {f !== 'all' && <span className="opacity-70">({rows.filter(r => r.status === f).length})</span>}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-slate-500 hover:text-[#3b4872] inline-flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* List */}
      <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">Auction</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Start</th>
              <th className="text-left p-3">End</th>
              <th className="text-right p-3">Invited</th>
              <th className="text-left p-3">Created by</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500">
                No auctions {filter !== 'all' ? `in ${filter}` : 'yet'}.{' '}
                {filter === 'all' && <button onClick={() => setCreateOpen(true)} className="text-[#3b4872] font-bold hover:underline">Create your first auction →</button>}
              </td></tr>
            )}
            {!loading && filtered.map(r => (
              <tr key={r.id} data-testid={`auction-row-${r.id}`}
                onClick={() => setSelectedId(r.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="p-3 font-mono text-xs font-bold">{r.auction_no}</td>
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3 text-xs text-slate-500">{fmt(r.start_at)}</td>
                <td className="p-3 text-xs text-slate-500">{fmt(r.end_at)}</td>
                <td className="p-3 text-right text-sm">{(r.invited_vendor_ids || []).length}</td>
                <td className="p-3 text-xs text-slate-500">{r.created_by || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateAuctionDialog open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(c) => { load(); setSelectedId(c.id); }} />
    </div>
  );
}
